import { viewingOtherUser } from './appUserContext.js';
import { qS } from '../_default.js';
import { activity } from '../activity.js';
import pluginList from '../../../../functions/plugins/pluginList.js';
import {
  createCustomPlugin,
  getPlugins,
  inspectCustomPlugin,
  startPluginConnection,
  startCustomPluginOAuth,
} from './appPlugin.js';
import { getLanguage, t } from './appTranslation.js';
import { buildCuratedPluginCatalog } from './pluginCatalog.mjs';
import { filterPluginCatalog } from './pluginSearch.mjs';
import { getPluginActivityMetadata } from './pluginActivity.mjs';
import customMcpToolAccess from '../../../../functions/plugins/customMcpToolAccess.js';
import customMcpHeaders from '../../../../functions/plugins/customMcpHeaders.js';

const { CUSTOM_MCP_MAX_HEADERS, normalizeCustomHeaders } = customMcpHeaders;

const {
  CUSTOM_MCP_TOOL_ACCESS,
  getCustomMcpToolDeclaredAccess,
} = customMcpToolAccess;

const CUSTOM_PLUGIN_ID = 'custom_mcp';
const CUSTOM_OAUTH_SETUP_STORAGE_KEY = 'customPluginOAuthSetup';
let selectedPlugin = null;
let pluginCatalog = [];
let customOAuthSetupId = '';
let customHeaderRowId = 0;
let customInspectionRevision = 0;

const inferInputKey = function(tool){
  const propertyList = Array.isArray(tool?.inputPropertyList) ? tool.inputPropertyList : [];
  return ['query', 'search', 'text', 'input', 'q'].find(key => propertyList.includes(key))
    || propertyList[0]
    || '';
};

const getSafeIconName = function(icon){
  return String(icon || 'custom').replace(/[^a-z0-9-]/gi, '').toLowerCase() || 'custom';
};

const createPluginLogo = function(pluginItem){
  const logo = document.createElement('span');
  logo.classList.add('plugin-option-logo', `plugin-option-logo-${getSafeIconName(pluginItem.icon)}`);
  logo.setAttribute('aria-hidden', 'true');
  if(pluginItem.toolkitSlug){
    logo.style.backgroundImage = `url("https://logos.composio.dev/api/${encodeURIComponent(pluginItem.toolkitSlug)}")`;
  } else if(![
    'custom', 'drive', 'figma', 'github', 'gmail',
    'atlassian-rovo', 'microsoft-365', 'notion', 'slack',
    'jira', 'confluence', 'outlook', 'onedrive', 'excel',
    'google-docs', 'google-sheets', 'google-slides',
  ].includes(getSafeIconName(pluginItem.icon))){
    logo.textContent = (pluginItem.name || '?').slice(0, 1);
  }
  return logo;
};

const ensurePluginFlow = function(){
  const container = qS('#plugin_flow_wrapper');
  if(!container || container.dataset.initialized === 'true'){
    return container;
  }

  container.innerHTML = `
    <section class="plugin-picker-section">
      <div class="plugin-section-heading">
        <div class="list-title-sub">${t('plugins.connectTo')}</div>
        <input class="plugin-catalog-search" id="plugin_catalog_search" type="text" autocomplete="off" aria-label="${t('plugins.searchPlaceholder')}" placeholder="${t('plugins.searchPlaceholder')}">
        <button class="plugin-select-another button-text hidden" type="button" id="plugin_selection_edit">${t('plugins.selectAnother')}</button>
      </div>
      <div class="list-text-sub">${t('plugins.copy')}</div>
      <div class="plugin-selected-summary hidden" id="plugin_selected_summary"></div>
      <div class="plugin-option-grid" id="plugin_option_grid">
        <div class="plugin-empty list-text-sub">${t('common.loading')}</div>
      </div>
    </section>

    <form class="plugin-details-form hidden" id="plugin_details_form">
      <section class="plugin-fields-section hidden" id="plugin_fields_section">
        <div class="input-label-top plugin-name-field">
          <label for="plugin_connection_name">${t('plugins.customName')}</label>
          <input id="plugin_connection_name" type="text" maxlength="80" required>
        </div>

        <div class="hidden" id="custom_mcp_fields">
          <div class="input-label-top">
            <label for="custom_mcp_endpoint">${t('plugins.customEndpoint')}</label>
            <input id="custom_mcp_endpoint" type="url" placeholder="https://mcp.example.com/mcp" required>
            <div class="plugin-field-help">${t('plugins.endpointHelp')}</div>
          </div>

          <div class="plugin-advanced-fields" id="custom_mcp_advanced_fields">
            <div class="input-label-top">
              <label for="custom_mcp_auth_type">${t('plugins.customAuthentication')}</label>
              <div class="select-wrapper">
                <select id="custom_mcp_auth_type">
                  <option value="none">${t('plugins.customNoAuthentication')}</option>
                  <option value="bearer">${t('plugins.customBearerToken')}</option>
                  <option value="oauth">${t('plugins.customOAuth')}</option>
                </select>
              </div>
            </div>
            <div class="input-label-top hidden" id="custom_mcp_token_container">
              <label for="custom_mcp_token">${t('plugins.customAccessToken')}</label>
              <input id="custom_mcp_token" type="password" autocomplete="off">
            </div>
            <div class="custom-mcp-oauth-fields hidden" id="custom_mcp_oauth_fields">
              <div class="input-label-top">
                <label for="custom_mcp_oauth_client_id">${t('plugins.customOAuthClientId')}</label>
                <input id="custom_mcp_oauth_client_id" type="text" maxlength="1000" autocomplete="off">
              </div>
              <div class="input-label-top">
                <label for="custom_mcp_oauth_client_secret">${t('plugins.customOAuthClientSecret')}</label>
                <input id="custom_mcp_oauth_client_secret" type="password" maxlength="4000" autocomplete="off">
              </div>
            </div>
          </div>

          <div class="plugin-advanced-fields" id="custom_mcp_headers_section">
            <div class="plugin-field-label">${t('plugins.customHeaders')}</div>
            <div class="plugin-field-help">${t('plugins.customHeadersHelp')}</div>
            <div class="custom-mcp-header-list" id="custom_mcp_header_list"></div>
            <button class="button button-secondary custom-mcp-add-header" type="button" id="custom_mcp_add_header">${t('plugins.customAddHeader')}</button>
          </div>

          <div class="custom-mcp-form-actions">
            <button class="button button-primary" type="button" id="custom_mcp_inspect">${t('plugins.customCheckConnection')}</button>
          </div>
          <div class="custom-mcp-tool-review hidden" id="custom_mcp_tool_review">
            <div class="list-title-sub">${t('plugins.customPermissions')}</div>
            <div class="custom-mcp-tool-list" id="custom_mcp_tool_list"></div>
            <div class="custom-mcp-unspecified-access hidden" id="custom_mcp_unspecified_access">
              <div class="title">${t('plugins.customUnspecifiedTitle')}</div>
              <div class="description">${t('plugins.customUnspecifiedCopy')}</div>
              <div class="segmented-control-container plugin-access-toggle">
                <input type="radio" name="custom_mcp_unspecified_access" id="custom_mcp_unspecified_read" value="read" checked>
                <label for="custom_mcp_unspecified_read">${t('plugins.customTreatAsRead')}</label>
                <input type="radio" name="custom_mcp_unspecified_access" id="custom_mcp_unspecified_write" value="write">
                <label for="custom_mcp_unspecified_write">${t('plugins.customTreatAsWrite')}</label>
              </div>
            </div>
          </div>
        </div>

        <div class="plugin-access-fields hidden" id="plugin_access_fields">
          <div class="plugin-field-label">${t('plugins.accessTitle')}</div>
          <div class="segmented-control-container plugin-access-toggle">
            <input type="radio" name="plugin_access" id="plugin_access_read" value="read" checked>
            <label for="plugin_access_read">${t('plugins.readOnly')}</label>
            <input type="radio" name="plugin_access" id="plugin_access_write" value="write">
            <label for="plugin_access_write">${t('plugins.readWrite')}</label>
          </div>
          <div class="plugin-access-capabilities" id="plugin_access_capabilities"></div>
        </div>
      </section>

      <div class="plugin-form-actions">
        <button class="button button-primary hidden" type="submit" id="custom_mcp_connect" disabled>${t('plugins.connect')}</button>
        <button class="button button-primary hidden" type="submit" id="plugin_connect">${t('plugins.connectButton')}</button>
      </div>
    </form>
    <div class="plugin-flow-error hidden" id="plugin_flow_error" role="alert"></div>
  `;
  container.dataset.initialized = 'true';
  initPluginForm();
  return container;
};

const showPluginFlowError = function(message = ''){
  const error = qS('#plugin_flow_error');
  if(!error){ return; }
  error.textContent = message;
  error.classList.toggle('hidden', !message);
};

const getCustomHeaderList = function(){
  return [...document.querySelectorAll('.custom-mcp-header-row')].map(row => ({
    name: row.querySelector('[data-header-name]').value,
    value: row.querySelector('[data-header-value]').value,
  }));
};

const customConnectionErrorMessage = function(error){
  const keys = {
    PLUGIN_TOKEN_KEY_NOT_CONFIGURED: 'plugins.customCredentialStorageUnavailable',
    PLUGINS_TOKEN_KEY_NOT_CONFIGURED: 'plugins.customCredentialStorageUnavailable',
    CUSTOM_MCP_HEADERS_INVALID: 'plugins.customHeadersInvalid',
    CUSTOM_MCP_HEADERS_RESERVED: 'plugins.customHeadersReserved',
    CUSTOM_MCP_HEADERS_DUPLICATE: 'plugins.customHeadersDuplicate',
    CUSTOM_MCP_HEADERS_LIMIT: 'plugins.customHeadersLimit',
  };
  return keys[error?.message] ? t(keys[error.message]) : null;
};

const validateCustomHeaders = function(){
  try {
    normalizeCustomHeaders(getCustomHeaderList());
    return true;
  } catch (error) {
    showPluginFlowError(customConnectionErrorMessage(error) || t('plugins.customCheckFailed'));
    return false;
  }
};

const syncCustomHeaderButton = function(){
  const button = qS('#custom_mcp_add_header');
  if(button){ button.disabled = getCustomHeaderList().length >= CUSTOM_MCP_MAX_HEADERS; }
};

const customHeadersChanged = function(){
  clearCustomOAuthSetup();
  resetCustomInspection();
  syncCustomHeaderButton();
};

const addCustomHeaderRow = function(){
  if(getCustomHeaderList().length >= CUSTOM_MCP_MAX_HEADERS){ return; }
  const id = ++customHeaderRowId;
  const row = document.createElement('div');
  row.classList.add('custom-mcp-header-row');
  row.innerHTML = `
    <div class="input-label-top">
      <label for="custom_mcp_header_name_${id}">${t('plugins.customHeaderName')}</label>
      <input id="custom_mcp_header_name_${id}" data-header-name type="text" maxlength="100" autocomplete="off" autocapitalize="none" spellcheck="false" placeholder="x-workspace-slug">
    </div>
    <div class="input-label-top">
      <label for="custom_mcp_header_value_${id}">${t('plugins.customHeaderValue')}</label>
      <input id="custom_mcp_header_value_${id}" data-header-value type="password" maxlength="8192" autocomplete="off" spellcheck="false">
    </div>
    <button class="button button-secondary" type="button">${t('plugins.customRemoveHeader')}</button>
  `;
  row.addEventListener('input', customHeadersChanged);
  row.querySelector('button').addEventListener('click', () => {
    row.remove();
    customHeadersChanged();
    qS('#custom_mcp_add_header')?.focus();
  });
  qS('#custom_mcp_header_list').appendChild(row);
  customHeadersChanged();
  row.querySelector('input').focus();
};

const getCustomConnectionDetails = function(){
  const selectedAuthType = qS('#custom_mcp_auth_type')?.value;
  const authType = ['bearer', 'oauth'].includes(selectedAuthType) ? selectedAuthType : 'none';
  return {
    name: qS('#plugin_connection_name')?.value || '',
    endpoint: qS('#custom_mcp_endpoint')?.value || '',
    authType,
    headerList: getCustomHeaderList(),
    accessToken: authType === 'bearer' ? qS('#custom_mcp_token')?.value || '' : '',
    oauthClientId: authType === 'oauth' ? qS('#custom_mcp_oauth_client_id')?.value || '' : '',
    oauthClientSecret: authType === 'oauth' ? qS('#custom_mcp_oauth_client_secret')?.value || '' : '',
    oauthSetupId: authType === 'oauth' ? customOAuthSetupId : '',
  };
};

const getPendingCustomOAuthSetup = function(){
  try {
    const value = JSON.parse(window.sessionStorage.getItem(CUSTOM_OAUTH_SETUP_STORAGE_KEY) || 'null');
    return value?.setupId && value?.endpoint && Array.isArray(value?.toolList) ? value : null;
  } catch (_error) {
    window.sessionStorage.removeItem(CUSTOM_OAUTH_SETUP_STORAGE_KEY);
    return null;
  }
};

const clearCustomOAuthSetup = function(){
  customOAuthSetupId = '';
  window.sessionStorage.removeItem(CUSTOM_OAUTH_SETUP_STORAGE_KEY);
  qS('#custom_mcp_headers_section')?.classList.remove('hidden');
};

const updateCustomAuthFields = function(){
  const authType = qS('#custom_mcp_auth_type')?.value || 'none';
  const bearer = authType === 'bearer';
  const oauth = authType === 'oauth';
  const oauthClientId = qS('#custom_mcp_oauth_client_id');
  const oauthClientSecret = qS('#custom_mcp_oauth_client_secret');
  qS('#custom_mcp_token_container')?.classList.toggle('hidden', !bearer);
  qS('#custom_mcp_oauth_fields')?.classList.toggle('hidden', !oauth);
  // OAuth keeps header values encrypted on the server across the redirect.
  qS('#custom_mcp_headers_section')?.classList.toggle('hidden', Boolean(customOAuthSetupId));
  qS('#custom_mcp_token').required = bearer;
  oauthClientSecret?.setCustomValidity(
    oauth && oauthClientSecret.value.trim() && !oauthClientId?.value.trim()
      ? t('plugins.customOAuthClientIdRequired')
      : ''
  );
  if(qS('#custom_mcp_inspect')){
    qS('#custom_mcp_inspect').textContent = t(oauth
      ? 'plugins.customContinueOAuth'
      : 'plugins.customCheckConnection');
  }
};

const syncCustomConnectButton = function(){
  const connectButton = qS('#custom_mcp_connect');
  if(!connectButton){ return; }
  const allowedRowList = [...document.querySelectorAll('.custom-mcp-tool-row')]
    .filter(row => row.querySelector('.custom-mcp-tool-permission')?.value === 'allowed');
  const hasAllowedUnspecifiedTool = allowedRowList.some(
    row => row.dataset.toolAccess === CUSTOM_MCP_TOOL_ACCESS.UNSPECIFIED
  );
  const unspecifiedAccess = qS('input[name="custom_mcp_unspecified_access"]:checked')?.value || '';
  const unspecifiedAccessContainer = qS('#custom_mcp_unspecified_access');
  unspecifiedAccessContainer?.classList.toggle('hidden', !hasAllowedUnspecifiedTool);
  document.querySelectorAll('input[name="custom_mcp_unspecified_access"]').forEach((input) => {
    input.required = hasAllowedUnspecifiedTool;
  });
  connectButton.disabled = allowedRowList.length === 0
    || (hasAllowedUnspecifiedTool && !unspecifiedAccess);
};

const renderCustomToolList = function(toolList){
  const list = qS('#custom_mcp_tool_list');
  list.innerHTML = '';
  toolList.forEach((tool) => {
    const row = document.createElement('div');
    row.classList.add('custom-mcp-tool-row');
    row.dataset.toolName = tool.name;
    row.dataset.inputKey = inferInputKey(tool);
    const declaredAccess = getCustomMcpToolDeclaredAccess(tool);
    row.dataset.toolAccess = declaredAccess;

    const details = document.createElement('div');
    details.classList.add('custom-mcp-tool-details');
    const title = document.createElement('div');
    title.classList.add('title');
    title.textContent = tool.name;
    details.appendChild(title);
    if(tool.description){
      const description = document.createElement('div');
      description.classList.add('description');
      description.textContent = tool.description;
      details.appendChild(description);
    }
    row.appendChild(details);

    const selectWrapper = document.createElement('div');
    selectWrapper.classList.add('select-wrapper');
    const select = document.createElement('select');
    select.classList.add('custom-mcp-tool-permission');
    [
      ['blocked', t('plugins.customDisallow')],
      ['allowed', t('plugins.customAllow')],
    ].forEach(([value, label]) => {
      const option = document.createElement('option');
      option.value = value;
      option.textContent = label;
      select.appendChild(option);
    });
    select.value = declaredAccess === CUSTOM_MCP_TOOL_ACCESS.READ ? 'allowed' : 'blocked';
    select.addEventListener('change', syncCustomConnectButton);
    selectWrapper.appendChild(select);
    row.appendChild(selectWrapper);
    list.appendChild(row);
  });
  qS('#custom_mcp_tool_review')?.classList.remove('hidden');
  qS('#custom_mcp_connect')?.classList.remove('hidden');
  syncCustomConnectButton();
};

const resetCustomInspection = function(){
  customInspectionRevision += 1;
  qS('#custom_mcp_tool_review')?.classList.add('hidden');
  qS('#custom_mcp_connect')?.classList.add('hidden');
  if(qS('#custom_mcp_tool_list')){
    qS('#custom_mcp_tool_list').innerHTML = '';
  }
  syncCustomConnectButton();
};

const setCustomFieldsEnabled = function(enabled){
  [
    '#custom_mcp_endpoint',
    '#custom_mcp_auth_type',
    '#custom_mcp_token',
    '#custom_mcp_oauth_client_id',
    '#custom_mcp_oauth_client_secret',
  ].forEach((selector) => {
    const field = qS(selector);
    if(field){ field.disabled = !enabled; }
  });
  document.querySelectorAll('#custom_mcp_headers_section input, #custom_mcp_headers_section button').forEach(field => {
    field.disabled = !enabled;
  });
  if(enabled){ syncCustomHeaderButton(); }
  const endpoint = qS('#custom_mcp_endpoint');
  if(endpoint){ endpoint.required = enabled; }
  const token = qS('#custom_mcp_token');
  if(token){
    token.required = enabled && qS('#custom_mcp_auth_type')?.value === 'bearer';
  }
};

const createCapabilityRow = function(copyText, type){
  const row = document.createElement('div');
  row.classList.add('plugin-capability-row', `plugin-capability-${type}`);
  const icon = document.createElement('span');
  icon.classList.add('plugin-capability-icon');
  icon.setAttribute('aria-hidden', 'true');
  row.appendChild(icon);
  const content = document.createElement('div');
  const copy = document.createElement('div');
  copy.classList.add('copy');
  copy.textContent = copyText;
  content.appendChild(copy);
  row.appendChild(content);
  return row;
};

const lowerFirst = function(value){
  const text = String(value || '').trim();
  if(getLanguage() === 'de'){
    return text;
  }
  return text ? `${text.charAt(0).toLocaleLowerCase()}${text.slice(1)}` : '';
};

const joinCapabilityList = function(capabilityList){
  const itemList = capabilityList.map(lowerFirst).filter(Boolean);
  if(itemList.length < 2){ return itemList[0] || ''; }
  if(itemList.length === 2){ return itemList.join(` ${t('common.and')} `); }
  return `${itemList.slice(0, -1).join(', ')}, ${t('common.and')} ${itemList.at(-1)}`;
};

const fallbackCapabilitySentence = function(prefixKey, capabilityList, fallback){
  const capabilityText = capabilityList.length
    ? joinCapabilityList(capabilityList)
    : lowerFirst(fallback);
  return t(
    prefixKey === 'plugins.cannot'
      ? 'plugins.capabilitiesBlocked'
      : 'plugins.capabilitiesAllowed',
    { capabilities: capabilityText }
  );
};

const renderAccessCapabilities = function(){
  const container = qS('#plugin_access_capabilities');
  if(!container || !selectedPlugin || selectedPlugin.custom){ return; }
  const allowWrite = qS('#plugin_access_write')?.checked === true;
  const readCapabilities = selectedPlugin.readCapabilityList || [];
  const writeCapabilities = selectedPlugin.writeCapabilityList || [];
  const capabilityCopy = selectedPlugin.capabilityCopy || {};
  container.innerHTML = '';
  container.appendChild(createCapabilityRow(
    capabilityCopy.read || fallbackCapabilitySentence(
      'plugins.can',
      readCapabilities,
      t('plugins.readConnectedData')
    ),
    'allowed'
  ));
  if(allowWrite && selectedPlugin.supportsWrite){
    container.appendChild(createCapabilityRow(
      capabilityCopy.write || fallbackCapabilitySentence(
        'plugins.can',
        writeCapabilities,
        t('plugins.connectedData')
      ),
      'allowed'
    ));
  }else{
    container.appendChild(createCapabilityRow(
      selectedPlugin.supportsWrite
        ? capabilityCopy.writeBlocked || fallbackCapabilitySentence(
          'plugins.cannot',
          writeCapabilities,
          t('plugins.connectedData')
        )
        : t('plugins.cannotChangeAnything'),
      'blocked'
    ));
  }
};

const renderSelectedSummary = function(pluginItem){
  const summary = qS('#plugin_selected_summary');
  summary.innerHTML = '';
  summary.appendChild(createPluginLogo(pluginItem));
  const body = document.createElement('div');
  const title = document.createElement('div');
  title.classList.add('plugin-option-title');
  title.textContent = pluginItem.name;
  body.appendChild(title);
  const description = document.createElement('div');
  description.classList.add('plugin-option-description');
  description.textContent = pluginItem.description;
  body.appendChild(description);
  summary.appendChild(body);
};

const selectPlugin = function(pluginItem){
  showPluginFlowError();
  const previousPluginId = selectedPlugin?.pluginId;
  selectedPlugin = pluginItem;
  const nameInput = qS('#plugin_connection_name');
  if(previousPluginId !== pluginItem.pluginId || !nameInput.value.trim()){
    const defaultName = pluginItem.custom ? '' : pluginItem.name;
    nameInput.value = defaultName;
    nameInput.dataset.defaultName = defaultName;
  }

  qS('#plugin_option_grid')?.classList.add('hidden');
  qS('#plugin_catalog_search')?.classList.add('hidden');
  qS('#plugin_selected_summary')?.classList.remove('hidden');
  qS('#plugin_selection_edit')?.classList.remove('hidden');
  qS('#plugin_details_form')?.classList.remove('hidden');
  qS('#plugin_fields_section')?.classList.remove('hidden');
  renderSelectedSummary(pluginItem);

  const customSelected = pluginItem.custom === true;
  qS('#custom_mcp_fields')?.classList.toggle('hidden', !customSelected);
  qS('#plugin_access_fields')?.classList.toggle('hidden', customSelected);
  qS('#plugin_connect')?.classList.toggle('hidden', customSelected);
  qS('#custom_mcp_connect')?.classList.add('hidden');
  setCustomFieldsEnabled(customSelected);
  resetCustomInspection();

  if(customSelected){
    updateCustomAuthFields();
    qS('#custom_mcp_endpoint')?.focus();
    return;
  }

  const writeInput = qS('#plugin_access_write');
  writeInput.disabled = pluginItem.supportsWrite !== true;
  qS('label[for="plugin_access_write"]')?.classList.toggle('disabled', writeInput.disabled);
  qS('#plugin_access_read').checked = true;
  renderAccessCapabilities();
  nameInput.focus();
  nameInput.select();
};

const showPluginSelection = function(){
  showPluginFlowError();
  clearCustomOAuthSetup();
  qS('#plugin_option_grid')?.classList.remove('hidden');
  qS('#plugin_catalog_search')?.classList.remove('hidden');
  qS('#plugin_selected_summary')?.classList.add('hidden');
  qS('#plugin_selection_edit')?.classList.add('hidden');
  qS('#plugin_details_form')?.classList.add('hidden');
  qS('#plugin_fields_section')?.classList.add('hidden');
};

const renderPluginOption = function(pluginItem){
  if(pluginItem.custom !== true && (pluginItem.active !== true || pluginItem.configured !== true)){
    return null;
  }
  const option = document.createElement('div');
  option.classList.add('plugin-option');
  const input = document.createElement('input');
  input.type = 'radio';
  input.name = 'plugin_target';
  input.id = `plugin_target_${pluginItem.pluginId}`;
  input.value = pluginItem.pluginId;
  const label = document.createElement('label');
  label.htmlFor = input.id;
  label.appendChild(createPluginLogo(pluginItem));
  const body = document.createElement('div');
  const title = document.createElement('div');
  title.classList.add('plugin-option-title');
  title.textContent = pluginItem.name;
  body.appendChild(title);
  const description = document.createElement('div');
  description.classList.add('plugin-option-description');
  description.textContent = pluginItem.description;
  body.appendChild(description);
  label.appendChild(body);
  option.appendChild(input);
  option.appendChild(label);
  label.addEventListener('click', (event) => {
    event.preventDefault();
    input.checked = true;
    selectPlugin(pluginItem);
    activity.log(
      activity.activityTypes.pluginView,
      getPluginActivityMetadata(pluginItem)
    );
  });
  return option;
};

const renderFilteredCatalog = function(){
  const grid = qS('#plugin_option_grid');
  if(!grid){ return; }
  const filteredList = filterPluginCatalog(
    pluginCatalog,
    qS('#plugin_catalog_search')?.value
  );
  grid.innerHTML = '';
  filteredList.forEach((pluginItem) => {
    const option = renderPluginOption(pluginItem);
    if(option){ grid.appendChild(option); }
  });
  if(!grid.childElementCount){
    const empty = document.createElement('div');
    empty.classList.add('plugin-empty', 'list-text-sub', 'plugin-search-empty');
    empty.textContent = t('plugins.noSearchResults');
    grid.appendChild(empty);
  }
};

const renderCatalog = async function({ preselectCustom = false, pendingOAuthSetup = null } = {}){
  const grid = qS('#plugin_option_grid');
  grid.classList.remove('hidden');
  grid.innerHTML = `<div class="plugin-empty list-text-sub">${t('common.loading')}</div>`;
  try {
    const serverPluginList = await getPlugins({ force: true });
    pluginCatalog = [
      ...buildCuratedPluginCatalog(pluginList, serverPluginList, getLanguage()),
      {
        pluginId: CUSTOM_PLUGIN_ID,
        name: t('plugins.customMcpTitle'),
        icon: 'custom',
        description: t('plugins.customDescription'),
        active: true,
        configured: true,
        custom: true,
        keywords: t('plugins.customSearchKeywords').split('|'),
      },
    ];
    renderFilteredCatalog();
    if(preselectCustom){
      selectPlugin(pluginCatalog.find(pluginItem => pluginItem.pluginId === CUSTOM_PLUGIN_ID));
      if(pendingOAuthSetup){
        const nameInput = qS('#plugin_connection_name');
        nameInput.value = pendingOAuthSetup.name || '';
        nameInput.dataset.defaultName = nameInput.value;
        qS('#custom_mcp_endpoint').value = pendingOAuthSetup.endpoint;
        qS('#custom_mcp_auth_type').value = 'oauth';
        qS('#custom_mcp_oauth_client_id').value = pendingOAuthSetup.oauthClientId || '';
        qS('#custom_mcp_oauth_client_secret').value = '';
        customOAuthSetupId = pendingOAuthSetup.setupId;
        updateCustomAuthFields();
        renderCustomToolList(pendingOAuthSetup.toolList);
      }
    }
  } catch (error) {
    grid.replaceChildren();
    showPluginFlowError(t('plugins.loadFailed'));
  }
};

const resetPluginForm = function({ clearError = true } = {}){
  if(clearError){ showPluginFlowError(); }
  selectedPlugin = null;
  customOAuthSetupId = '';
  if(qS('#plugin_catalog_search')){ qS('#plugin_catalog_search').value = ''; }
  const form = qS('#plugin_details_form');
  form?.reset();
  qS('#custom_mcp_header_list')?.replaceChildren();
  syncCustomHeaderButton();
  form?.classList.add('hidden');
  qS('#plugin_fields_section')?.classList.add('hidden');
  qS('#plugin_selected_summary')?.classList.add('hidden');
  qS('#plugin_selection_edit')?.classList.add('hidden');
  qS('#plugin_catalog_search')?.classList.remove('hidden');
  qS('#custom_mcp_fields')?.classList.add('hidden');
  qS('#plugin_access_fields')?.classList.add('hidden');
  qS('#plugin_connect')?.classList.add('hidden');
  qS('#custom_mcp_token_container')?.classList.add('hidden');
  qS('#custom_mcp_oauth_fields')?.classList.add('hidden');
  setCustomFieldsEnabled(false);
  resetCustomInspection();
};

const submitCustomPlugin = async function(){
  const connectButton = qS('#custom_mcp_connect');
  const toolSelectionList = [...document.querySelectorAll('.custom-mcp-tool-row')].map(row => ({
    name: row.dataset.toolName,
    inputKey: row.dataset.inputKey,
    allowed: row.querySelector('.custom-mcp-tool-permission')?.value === 'allowed',
    declaredAccess: row.dataset.toolAccess,
  }));
  const unspecifiedToolAccess = qS('input[name="custom_mcp_unspecified_access"]:checked')?.value || null;
  connectButton.classList.add('button-loading');
  connectButton.disabled = true;
  try {
    await createCustomPlugin({
      ...getCustomConnectionDetails(),
      toolSelectionList,
      unspecifiedToolAccess,
    });
    clearCustomOAuthSetup();
    resetPluginForm();
    await renderCatalog();
    const { renderPluginList } = await import('./appPluginRender.js');
    await renderPluginList();
  } catch (error) {
    console.error('Custom MCP connection failed:', error);
    showPluginFlowError(customConnectionErrorMessage(error) || t('plugins.customConnectFailed'));
    connectButton.classList.remove('button-loading');
    syncCustomConnectButton();
  }
};

const submitCuratedPlugin = async function(){
  const connectButton = qS('#plugin_connect');
  connectButton.classList.add('button-loading');
  connectButton.disabled = true;
  try {
    const result = await startPluginConnection(selectedPlugin.pluginId, {
      name: qS('#plugin_connection_name')?.value || selectedPlugin.name,
      allowWriteActions: qS('#plugin_access_write')?.checked === true,
    });
    if(result.noAuth){
      resetPluginForm();
      await renderCatalog();
      const { renderPluginList } = await import('./appPluginRender.js');
      await renderPluginList();
    }
  } catch (error) {
    showPluginFlowError(error.message || t('plugins.connectFailed'));
    connectButton.classList.remove('button-loading');
    connectButton.disabled = false;
  }
};

const initPluginForm = function(){
  const form = qS('#plugin_details_form');
  const inspectButton = qS('#custom_mcp_inspect');
  const authType = qS('#custom_mcp_auth_type');

  qS('#custom_mcp_add_header')?.addEventListener('click', addCustomHeaderRow);

  qS('#plugin_selection_edit')?.addEventListener('click', showPluginSelection);
  qS('#plugin_catalog_search')?.addEventListener('input', renderFilteredCatalog);
  qS('#plugin_access_read')?.addEventListener('change', renderAccessCapabilities);
  qS('#plugin_access_write')?.addEventListener('change', renderAccessCapabilities);
  document.querySelectorAll('input[name="custom_mcp_unspecified_access"]').forEach((input) => {
    input.addEventListener('change', syncCustomConnectButton);
  });

  authType?.addEventListener('change', () => {
    clearCustomOAuthSetup();
    updateCustomAuthFields();
    resetCustomInspection();
  });
  [
    qS('#custom_mcp_endpoint'),
    qS('#custom_mcp_token'),
    qS('#custom_mcp_oauth_client_id'),
    qS('#custom_mcp_oauth_client_secret'),
  ].forEach(input => {
    input?.addEventListener('input', () => {
      clearCustomOAuthSetup();
      updateCustomAuthFields();
      resetCustomInspection();
    });
  });

  inspectButton?.addEventListener('click', async () => {
    showPluginFlowError();
    const details = getCustomConnectionDetails();
    const revision = customInspectionRevision;
    const endpointValid = qS('#custom_mcp_endpoint')?.reportValidity() !== false;
    const tokenValid = qS('#custom_mcp_token')?.reportValidity() !== false;
    const clientIdValid = qS('#custom_mcp_oauth_client_id')?.reportValidity() !== false;
    const clientSecretValid = qS('#custom_mcp_oauth_client_secret')?.reportValidity() !== false;
    if(!endpointValid || !tokenValid || !clientIdValid || !clientSecretValid || !validateCustomHeaders()){ return; }
    inspectButton.classList.add('button-loading');
    inspectButton.disabled = true;
    try {
      if(details.authType === 'oauth'){
        await startCustomPluginOAuth(details);
        return;
      }
      const result = await inspectCustomPlugin(details);
      if(revision !== customInspectionRevision){ return; }
      const nameInput = qS('#plugin_connection_name');
      if(result.serverName && nameInput.value === nameInput.dataset.defaultName){
        nameInput.value = result.serverName;
        nameInput.dataset.defaultName = result.serverName;
      }
      renderCustomToolList(result.toolList || []);
    } catch (error) {
      showPluginFlowError(customConnectionErrorMessage(error) || t('plugins.customCheckFailed'));
    } finally {
      inspectButton.classList.remove('button-loading');
      inspectButton.disabled = false;
    }
  });

  form?.addEventListener('submit', async (event) => {
    event.preventDefault();
    showPluginFlowError();
    if(!selectedPlugin || !form.reportValidity()){ return; }
    if(selectedPlugin.custom){
      if(!validateCustomHeaders()){ return; }
      await submitCustomPlugin();
      return;
    }
    await submitCuratedPlugin();
  });
};

const renderPluginFlow = function({ preselectCustom = false } = {}){
  const container = ensurePluginFlow();
  if(!container){ return; }
  container.classList.toggle('hidden', viewingOtherUser());
  if(viewingOtherUser()){ return; }
  // OAuth callback errors are set during initialization, before this first render.
  resetPluginForm({ clearError: false });
  const pendingOAuthSetup = getPendingCustomOAuthSetup();
  return renderCatalog({
    preselectCustom: preselectCustom || Boolean(pendingOAuthSetup),
    pendingOAuthSetup,
  });
};

export {
  ensurePluginFlow,
  renderPluginFlow,
  showPluginFlowError,
};
